import React, { useEffect, lazy } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/StructuredData';
export function SpringDeepCleaning() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen w-full font-sans bg-white flex flex-col" data-id="element-759">
      <SEOHead title="Spring / Deep Cleaning | Smith & Jengs Cleaning Services" description="Thorough spring and deep cleaning services in Bicester and Oxfordshire. Refresh your home with our meticulous attention to detail." canonical="https://smithandjengs.com/spring-deep-cleaning" data-id="element-760" />
      <BreadcrumbSchema items={[{
      name: 'Home',
      url: 'https://smithandjengs.com/'
    }, {
      name: 'Spring / Deep Cleaning',
      url: 'https://smithandjengs.com/spring-deep-cleaning'
    }]} data-id="element-761" />
      <Navbar data-id="element-762" />

      <main className="flex-1" data-id="element-763">
        {/* 1. Hero Banner */}
        <section className="relative w-full h-[55vh] min-h-[420px] max-h-[600px] overflow-hidden bg-[#1a2744]" data-id="element-764">
          <div className="absolute inset-0" data-id="element-765">
            <img src="https://cdn.magicpatterns.com/uploads/pwzkMp41NqfVwFtPzbYese/sj-website-deep-clean-banner.png" alt="Window cleaning with squeegee" className="w-full h-full object-cover object-center" data-id="element-766" />
            {/* Dark gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a2744]/90 via-[#1a2744]/50 to-transparent" data-id="element-767" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a2744]/70 via-transparent to-transparent" data-id="element-768" />
          </div>

          <div className="relative z-10 h-full flex items-end px-6 md:px-12 pb-12 pt-32" data-id="element-769">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} className="max-w-7xl mx-auto w-full" data-id="element-770">
              <h1 className="font-bebas text-white leading-[0.85] tracking-wide" data-id="element-771">
                <span className="text-7xl md:text-[9rem] lg:text-[11rem] block" data-id="element-772">
                  SPRING / DEEP
                </span>
                <span className="font-serif-playfair italic font-normal normal-case text-[#e91e8c] text-6xl md:text-[8rem] lg:text-[10rem] block -mt-2 md:-mt-6" data-id="element-773">
                  Cleaning
                </span>
              </h1>
            </motion.div>
          </div>
        </section>

        {/* 2. Description + Image Section */}
        <section className="w-full bg-white py-16 md:py-24 px-6 md:px-12" data-id="element-774">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center" data-id="element-775">
            {/* Left: Text */}
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="flex flex-col lg:max-w-md" data-id="element-776">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2744] mb-6 leading-tight" data-id="element-777">
                A thorough refresh
                <br data-id="element-778" />
                for your entire
                <br data-id="element-779" />
                home.
              </h2>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed" data-id="element-780">
                Sometimes your home needs more than just a standard clean. Our
                deep cleaning service tackles the hard-to-reach areas and
                built-up grime, restoring your home to its sparkling best.
                Perfect for seasonal cleaning or preparing for special
                occasions.
              </p>
            </motion.div>

            {/* Right: Image */}
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="w-full h-[400px] lg:h-[500px] rounded-sm overflow-hidden" data-id="element-781">
              <img src="https://cdn.magicpatterns.com/uploads/5UDPVEhMavWJE3eTgmz97T/sj-website-deep-clean-img1.png" alt="Colorful cleaning products" className="w-full h-full object-cover" loading="lazy" data-id="element-782" />
            </motion.div>
          </div>
        </section>

        {/* 3. Services List Section */}
        <section className="w-full bg-white pb-16 md:pb-24 px-6 md:px-12" data-id="element-783">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center" data-id="element-784">
            {/* Left: Image */}
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="w-full h-[400px] lg:h-[500px] rounded-sm overflow-hidden order-2 lg:order-1" data-id="element-785">
              <img src="https://cdn.magicpatterns.com/uploads/3BExEzShNNTHuhZFbGeLh6/sj-website-deep-clean-img2.png" alt="Deep cleaning a bathroom sink" className="w-full h-full object-cover" loading="lazy" data-id="element-786" />
            </motion.div>

            {/* Right: List */}
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="flex flex-col order-1 lg:order-2" data-id="element-787">
              <ul className="flex flex-col w-full" data-id="element-788">
                {['Inside cabinets and drawers', 'Baseboards and door frames', 'Behind and under appliances', 'Detailed dusting of all surfaces'].map((item, i) => <li key={i} className="border-b border-gray-300 py-6 text-[#1a2744] text-lg md:text-xl font-medium first:pt-0" data-id="element-789">
                    {item}
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </section>

        {/* 4. CTA Section */}
        <section className="w-full bg-[#f8f9fa] py-16 px-6 md:px-12" data-id="element-790">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 text-center md:text-left" data-id="element-791">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2744] leading-tight" data-id="element-792">
              Click <span className="text-[#e91e8c]" data-id="element-793">Book Online</span> to set
              up
              <br className="hidden md:block" data-id="element-794" /> a Spring / Deep Cleaning
            </h2>
            <Link to="/contact-us" className="bg-[#e91e8c] hover:bg-[#d0187c] text-white px-8 py-4 rounded-full font-semibold tracking-wide transition-colors shadow-md flex items-center gap-3 whitespace-nowrap" data-id="element-795">
              Book Online <span className="text-xl leading-none" data-id="element-796">&rarr;</span>
            </Link>
          </motion.div>
        </section>
      </main>

      <Footer data-id="element-797" />
    </div>;
}